/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Graph Domain Commands
   Explicit, fully undoable domain operations.
   ═══════════════════════════════════════════════════════════════ */

import type {
  DomainGraphState,
  EdgeId,
  EdgeInstance,
  NodeId,
  NodeInstance,
  PortId,
  Position,
} from '../types';
import { generateStableId } from '../id';
import {
  addNode,
  removeNode,
  updateNodePosition,
  updateNodeParam,
  renameNode,
  addEdge,
  removeEdge,
} from '../graph';
import { getPortKey } from '../indexes';
import type { CommandDescriptor, DomainCommand } from './types';

// ── 1. Create Node Command ──

export class CreateNodeCommand implements DomainCommand {
  readonly descriptor: CommandDescriptor<{ node: NodeInstance }>;

  constructor(public readonly node: NodeInstance, label?: string) {
    this.descriptor = {
      id: generateStableId('cmd'),
      type: 'node.create',
      label: label || `Create ${node.meta?.label || node.type}`,
      timestamp: Date.now(),
      payload: { node },
    };
  }

  execute(state: DomainGraphState): DomainGraphState {
    return addNode(state, this.node);
  }

  undo(state: DomainGraphState): DomainGraphState {
    return removeNode(state, this.node.id);
  }
}

// ── 2. Delete Node Command ──

export class DeleteNodeCommand implements DomainCommand {
  readonly descriptor: CommandDescriptor<{
    nodeId: NodeId;
    deletedNode?: NodeInstance;
    deletedEdges?: EdgeInstance[];
  }>;

  private capturedNode?: NodeInstance;
  private capturedEdges: EdgeInstance[] = [];

  constructor(public readonly nodeId: NodeId) {
    this.descriptor = {
      id: generateStableId('cmd'),
      type: 'node.delete',
      label: `Delete Node ${nodeId}`,
      timestamp: Date.now(),
      payload: { nodeId },
    };
  }

  execute(state: DomainGraphState): DomainGraphState {
    this.capturedNode = state.canonical.nodes[this.nodeId];
    if (!this.capturedNode) return state;

    // Capture all connected edges so undo can resurrect them exactly
    this.capturedEdges = Object.values(state.canonical.edges).filter(
      (e) => e.sourceNodeId === this.nodeId || e.targetNodeId === this.nodeId
    );

    return removeNode(state, this.nodeId);
  }

  undo(state: DomainGraphState): DomainGraphState {
    if (!this.capturedNode) return state;

    // Re-add node
    let nextState = addNode(state, this.capturedNode);

    // Re-add captured edges
    for (const edge of this.capturedEdges) {
      const res = addEdge(
        nextState,
        edge.sourceNodeId,
        edge.sourcePortId,
        edge.targetNodeId,
        edge.targetPortId,
        edge.id
      );
      if (!res.error) {
        nextState = res.state;
      }
    }

    return nextState;
  }
}

// ── 3. Move Node Command (Mergeable) ──

export class MoveNodeCommand implements DomainCommand {
  readonly descriptor: CommandDescriptor<{
    nodeId: NodeId;
    from: Position;
    to: Position;
    sessionId?: string;
  }>;

  constructor(
    public readonly nodeId: NodeId,
    public readonly from: Position,
    public readonly to: Position,
    public readonly isMergeable = true,
    public readonly sessionId?: string
  ) {
    this.descriptor = {
      id: generateStableId('cmd'),
      type: 'node.move',
      label: `Move Node`,
      timestamp: Date.now(),
      payload: { nodeId, from, to, sessionId },
      mergeable: isMergeable,
    };
  }

  execute(state: DomainGraphState): DomainGraphState {
    return updateNodePosition(state, this.nodeId, this.to);
  }

  undo(state: DomainGraphState): DomainGraphState {
    return updateNodePosition(state, this.nodeId, this.from);
  }

  mergeWith(previous: DomainCommand): DomainCommand | null {
    if (
      this.isMergeable &&
      previous instanceof MoveNodeCommand &&
      previous.isMergeable &&
      previous.nodeId === this.nodeId
    ) {
      if (this.sessionId && previous.sessionId) {
        if (this.sessionId === previous.sessionId) {
          return new MoveNodeCommand(this.nodeId, previous.from, this.to, this.isMergeable, this.sessionId);
        }
        return null;
      }
      if (this.descriptor.timestamp - previous.descriptor.timestamp < 1000) {
        return new MoveNodeCommand(this.nodeId, previous.from, this.to, this.isMergeable, this.sessionId);
      }
    }
    return null;
  }
}

export class ToggleNodeCollapseCommand implements DomainCommand {
  readonly descriptor: CommandDescriptor<{ nodeId: NodeId; collapsed?: boolean }>;
  private previousState = false;

  constructor(public readonly nodeId: NodeId, public readonly collapsed?: boolean) {
    this.descriptor = {
      id: generateStableId('cmd'),
      type: 'node.collapse',
      label: `Toggle Node Collapse ${nodeId}`,
      timestamp: Date.now(),
      payload: { nodeId, collapsed },
    };
  }

  execute(state: DomainGraphState): DomainGraphState {
    const node = state.canonical.nodes[this.nodeId];
    if (!node) return state;
    this.previousState = Boolean(node.collapsed);
    return addNode(state, { ...node, collapsed: this.collapsed ?? !this.previousState });
  }

  undo(state: DomainGraphState): DomainGraphState {
    const node = state.canonical.nodes[this.nodeId];
    if (!node) return state;
    return addNode(state, { ...node, collapsed: this.previousState });
  }
}

// ── 4. Rename Node Command ──

export class RenameNodeCommand implements DomainCommand {
  readonly descriptor: CommandDescriptor<{
    nodeId: NodeId;
    oldLabel: string;
    newLabel: string;
  }>;

  constructor(
    public readonly nodeId: NodeId,
    public readonly oldLabel: string,
    public readonly newLabel: string
  ) {
    this.descriptor = {
      id: generateStableId('cmd'),
      type: 'node.rename',
      label: `Rename to "${newLabel}"`,
      timestamp: Date.now(),
      payload: { nodeId, oldLabel, newLabel },
    };
  }

  execute(state: DomainGraphState): DomainGraphState {
    return renameNode(state, this.nodeId, this.newLabel);
  }

  undo(state: DomainGraphState): DomainGraphState {
    return renameNode(state, this.nodeId, this.oldLabel);
  }
}

// ── 5. Set Parameter Command (Mergeable) ──

export class SetParameterCommand implements DomainCommand {
  readonly descriptor: CommandDescriptor<{
    nodeId: NodeId;
    paramId: string;
    oldValue: unknown;
    newValue: unknown;
    sessionId?: string;
  }>;

  constructor(
    public readonly nodeId: NodeId,
    public readonly paramId: string,
    public readonly oldValue: unknown,
    public readonly newValue: unknown,
    public readonly isMergeable = true,
    public readonly sessionId?: string
  ) {
    this.descriptor = {
      id: generateStableId('cmd'),
      type: 'param.set',
      label: `Set ${paramId}`,
      timestamp: Date.now(),
      payload: { nodeId, paramId, oldValue, newValue, sessionId },
      mergeable: isMergeable,
    };
  }

  execute(state: DomainGraphState): DomainGraphState {
    return updateNodeParam(state, this.nodeId, this.paramId, this.newValue);
  }

  undo(state: DomainGraphState): DomainGraphState {
    return updateNodeParam(state, this.nodeId, this.paramId, this.oldValue);
  }

  mergeWith(previous: DomainCommand): DomainCommand | null {
    if (
      this.isMergeable &&
      previous instanceof SetParameterCommand &&
      previous.isMergeable &&
      previous.nodeId === this.nodeId &&
      previous.paramId === this.paramId
    ) {
      if (this.sessionId && previous.sessionId) {
        if (this.sessionId === previous.sessionId) {
          return new SetParameterCommand(
            this.nodeId,
            this.paramId,
            previous.oldValue,
            this.newValue,
            this.isMergeable,
            this.sessionId
          );
        }
        return null;
      }
      if (this.descriptor.timestamp - previous.descriptor.timestamp < 1000) {
        return new SetParameterCommand(
          this.nodeId,
          this.paramId,
          previous.oldValue,
          this.newValue,
          this.isMergeable,
          this.sessionId
        );
      }
    }
    return null;
  }
}

// ── 6. Connect Ports Command ──

export class ConnectPortsCommand implements DomainCommand {
  readonly descriptor: CommandDescriptor<{
    sourceNodeId: NodeId;
    sourcePortId: PortId;
    targetNodeId: NodeId;
    targetPortId: PortId;
    edgeId?: EdgeId;
  }>;

  private createdEdgeId?: EdgeId;
  private replacedEdge?: EdgeInstance;

  constructor(
    public readonly sourceNodeId: NodeId,
    public readonly sourcePortId: PortId,
    public readonly targetNodeId: NodeId,
    public readonly targetPortId: PortId,
    edgeId?: EdgeId
  ) {
    this.createdEdgeId = edgeId;
    this.descriptor = {
      id: generateStableId('cmd'),
      type: 'ports.connect',
      label: `Connect ${sourcePortId} → ${targetPortId}`,
      timestamp: Date.now(),
      payload: { sourceNodeId, sourcePortId, targetNodeId, targetPortId, edgeId },
    };
  }

  execute(state: DomainGraphState): DomainGraphState {
    const targetNode = state.canonical.nodes[this.targetNodeId];
    const targetPort = targetNode?.inputs[this.targetPortId];
    const targetPortKey = getPortKey(this.targetNodeId, this.targetPortId);
    const existingEdges = state.indexes.portConnections[targetPortKey] || [];

    let workingState = state;
    // Reconnect: If target port is single-input and already has an edge, replace it
    if (targetPort && !targetPort.multi && existingEdges.length > 0) {
      const oldEdgeId = existingEdges[0];
      this.replacedEdge = state.canonical.edges[oldEdgeId];
      if (this.replacedEdge) {
        workingState = removeEdge(workingState, oldEdgeId);
      }
    }

    const res = addEdge(
      workingState,
      this.sourceNodeId,
      this.sourcePortId,
      this.targetNodeId,
      this.targetPortId,
      this.createdEdgeId
    );

    if (res.error) {
      return state;
    }

    this.createdEdgeId = res.edgeId;
    return res.state;
  }

  undo(state: DomainGraphState): DomainGraphState {
    let workingState = state;
    if (this.createdEdgeId) {
      workingState = removeEdge(workingState, this.createdEdgeId);
    }
    // Restore the replaced edge if one existed
    if (this.replacedEdge) {
      const restoreRes = addEdge(
        workingState,
        this.replacedEdge.sourceNodeId,
        this.replacedEdge.sourcePortId,
        this.replacedEdge.targetNodeId,
        this.replacedEdge.targetPortId,
        this.replacedEdge.id
      );
      if (restoreRes.state) {
        workingState = restoreRes.state;
      }
    }
    return workingState;
  }
}

// ── 7. Disconnect Edge Command ──

export class DisconnectEdgeCommand implements DomainCommand {
  readonly descriptor: CommandDescriptor<{ edgeId: EdgeId; edge?: EdgeInstance }>;

  private capturedEdge?: EdgeInstance;

  constructor(public readonly edgeId: EdgeId) {
    this.descriptor = {
      id: generateStableId('cmd'),
      type: 'edge.disconnect',
      label: `Disconnect Wire`,
      timestamp: Date.now(),
      payload: { edgeId },
    };
  }

  execute(state: DomainGraphState): DomainGraphState {
    this.capturedEdge = state.canonical.edges[this.edgeId];
    if (!this.capturedEdge) return state;
    return removeEdge(state, this.edgeId);
  }

  undo(state: DomainGraphState): DomainGraphState {
    if (!this.capturedEdge) return state;

    const res = addEdge(
      state,
      this.capturedEdge.sourceNodeId,
      this.capturedEdge.sourcePortId,
      this.capturedEdge.targetNodeId,
      this.capturedEdge.targetPortId,
      this.capturedEdge.id
    );

    return res.error ? state : res.state;
  }
}

// ── 8. Batch / Composite Command ──

export class BatchCommand implements DomainCommand {
  readonly descriptor: CommandDescriptor<{ count: number }>;

  constructor(public readonly commands: readonly DomainCommand[], label = 'Batch Operation') {
    this.descriptor = {
      id: generateStableId('cmd'),
      type: 'batch',
      label,
      timestamp: Date.now(),
      payload: { count: commands.length },
    };
  }

  execute(state: DomainGraphState): DomainGraphState {
    let current = state;
    for (const cmd of this.commands) {
      current = cmd.execute(current);
    }
    return current;
  }

  undo(state: DomainGraphState): DomainGraphState {
    let current = state;
    // Undo in reverse order
    for (let i = this.commands.length - 1; i >= 0; i--) {
      current = this.commands[i].undo(current);
    }
    return current;
  }
}

// ── 9. Bypass Node Command ──

export class BypassNodeCommand implements DomainCommand {
  readonly descriptor: CommandDescriptor<{ nodeId: NodeId; bypassed: boolean }>;
  private previousState = false;

  constructor(public readonly nodeId: NodeId, public readonly bypassed?: boolean) {
    this.descriptor = {
      id: generateStableId('cmd'),
      type: 'node.bypass',
      label: `Toggle Bypass Node ${nodeId}`,
      timestamp: Date.now(),
      payload: { nodeId, bypassed: bypassed ?? true },
    };
  }

  execute(state: DomainGraphState): DomainGraphState {
    const node = state.canonical.nodes[this.nodeId];
    if (!node) return state;
    this.previousState = Boolean(node.bypassed);
    const nextBypassed = this.bypassed !== undefined ? this.bypassed : !this.previousState;
    return addNode(state, {
      ...node,
      bypassed: nextBypassed,
    });
  }

  undo(state: DomainGraphState): DomainGraphState {
    const node = state.canonical.nodes[this.nodeId];
    if (!node) return state;
    return addNode(state, {
      ...node,
      bypassed: this.previousState,
    });
  }
}

// ── 10. Move Multiple Nodes Command ──

export interface NodeDeltaMove {
  readonly nodeId: NodeId;
  readonly from: Position;
  readonly to: Position;
}

export class MoveMultipleNodesCommand implements DomainCommand {
  readonly descriptor: CommandDescriptor<{ moves: readonly NodeDeltaMove[]; sessionId?: string }>;

  constructor(
    public readonly moves: readonly NodeDeltaMove[],
    public readonly isMergeable = true,
    public readonly sessionId?: string
  ) {
    this.descriptor = {
      id: generateStableId('cmd'),
      type: 'nodes.move_multiple',
      label: `Move ${moves.length} Nodes`,
      timestamp: Date.now(),
      payload: { moves, sessionId },
      mergeable: isMergeable,
    };
  }

  execute(state: DomainGraphState): DomainGraphState {
    let current = state;
    for (const move of this.moves) {
      current = updateNodePosition(current, move.nodeId, move.to);
    }
    return current;
  }

  undo(state: DomainGraphState): DomainGraphState {
    let current = state;
    for (const move of this.moves) {
      current = updateNodePosition(current, move.nodeId, move.from);
    }
    return current;
  }

  mergeWith(previous: DomainCommand): DomainCommand | null {
    if (
      this.isMergeable &&
      previous instanceof MoveMultipleNodesCommand &&
      previous.isMergeable &&
      previous.moves.length === this.moves.length
    ) {
      if (this.sessionId && previous.sessionId && this.sessionId === previous.sessionId) {
        // Match up previous initial positions with current targets
        const mergedMoves: NodeDeltaMove[] = [];
        const prevMoveMap = new Map(previous.moves.map((m) => [m.nodeId, m.from]));
        for (const curr of this.moves) {
          const initialFrom = prevMoveMap.get(curr.nodeId);
          if (initialFrom) {
            mergedMoves.push({
              nodeId: curr.nodeId,
              from: initialFrom,
              to: curr.to,
            });
          } else {
            return null;
          }
        }
        return new MoveMultipleNodesCommand(mergedMoves, this.isMergeable, this.sessionId);
      }
    }
    return null;
  }
}

// ── 11. Clipboard Fragment & Paste Command ──

export interface GraphClipboardFragment {
  readonly nodes: readonly NodeInstance[];
  readonly internalEdges: readonly EdgeInstance[];
}

export class PasteFragmentCommand implements DomainCommand {
  readonly descriptor: CommandDescriptor<{ count: number }>;
  private createdNodeIds: NodeId[] = [];
  private createdEdgeIds: EdgeId[] = [];

  constructor(
    public readonly fragment: GraphClipboardFragment,
    public readonly offset: Position = { x: 40, y: 40 }
  ) {
    this.descriptor = {
      id: generateStableId('cmd'),
      type: 'graph.paste',
      label: `Paste ${fragment.nodes.length} Nodes`,
      timestamp: Date.now(),
      payload: { count: fragment.nodes.length },
    };
  }

  execute(state: DomainGraphState): DomainGraphState {
    let current = state;
    const idMap = new Map<NodeId, NodeId>();
    this.createdNodeIds = [];
    this.createdEdgeIds = [];

    // 1. Create duplicate nodes with fresh IDs and offset positions
    for (const originalNode of this.fragment.nodes) {
      const newId = generateStableId('node');
      idMap.set(originalNode.id, newId);
      this.createdNodeIds.push(newId);

      const clonedNode: NodeInstance = {
        ...originalNode,
        id: newId,
        position: {
          x: originalNode.position.x + this.offset.x,
          y: originalNode.position.y + this.offset.y,
        },
        meta: {
          ...originalNode.meta,
          label: originalNode.meta?.label ? `${originalNode.meta.label}` : originalNode.type,
        },
      };

      current = addNode(current, clonedNode);
    }

    // 2. Re-create internal edges with mapped IDs
    for (const edge of this.fragment.internalEdges) {
      const newSourceId = idMap.get(edge.sourceNodeId);
      const newTargetId = idMap.get(edge.targetNodeId);

      if (newSourceId && newTargetId) {
        const edgeRes = addEdge(
          current,
          newSourceId,
          edge.sourcePortId,
          newTargetId,
          edge.targetPortId
        );
        if (!edgeRes.error && edgeRes.edgeId) {
          current = edgeRes.state;
          this.createdEdgeIds.push(edgeRes.edgeId);
        }
      }
    }

    return current;
  }

  undo(state: DomainGraphState): DomainGraphState {
    let current = state;
    // Remove edges first
    for (const edgeId of this.createdEdgeIds) {
      current = removeEdge(current, edgeId);
    }
    // Remove nodes
    for (const nodeId of this.createdNodeIds) {
      current = removeNode(current, nodeId);
    }
    return current;
  }
}

// ── 12. Reset Parameter Command ──

export class ResetParamCommand implements DomainCommand {
  readonly descriptor: CommandDescriptor<{ nodeId: NodeId; paramId: string; oldValue: unknown; defaultValue: unknown }>;

  constructor(
    public readonly nodeId: NodeId,
    public readonly paramId: string,
    public readonly oldValue: unknown,
    public readonly defaultValue: unknown
  ) {
    this.descriptor = {
      id: generateStableId('cmd'),
      type: 'param.reset',
      label: `Reset ${paramId}`,
      timestamp: Date.now(),
      payload: { nodeId, paramId, oldValue, defaultValue },
    };
  }

  execute(state: DomainGraphState): DomainGraphState {
    return updateNodeParam(state, this.nodeId, this.paramId, this.defaultValue);
  }

  undo(state: DomainGraphState): DomainGraphState {
    return updateNodeParam(state, this.nodeId, this.paramId, this.oldValue);
  }
}

// ── 13. Frame Management Commands ──

export class CreateFrameCommand implements DomainCommand {
  readonly descriptor: CommandDescriptor<{ frame: import('../types').FrameInstance }>;

  constructor(public readonly frame: import('../types').FrameInstance) {
    this.descriptor = {
      id: generateStableId('cmd'),
      type: 'frame.create',
      label: `Create Frame "${frame.title}"`,
      timestamp: Date.now(),
      payload: { frame },
    };
  }

  execute(state: DomainGraphState): DomainGraphState {
    const frames = {
      ...(state.canonical.frames || {}),
      [this.frame.id]: this.frame,
    };
    return {
      canonical: {
        ...state.canonical,
        frames,
        modifiedAt: Date.now(),
      },
      indexes: state.indexes,
    };
  }

  undo(state: DomainGraphState): DomainGraphState {
    if (!state.canonical.frames || !state.canonical.frames[this.frame.id]) {
      return state;
    }
    const frames = { ...state.canonical.frames };
    delete frames[this.frame.id];
    return {
      canonical: {
        ...state.canonical,
        frames,
        modifiedAt: Date.now(),
      },
      indexes: state.indexes,
    };
  }
}

export class DeleteFrameCommand implements DomainCommand {
  readonly descriptor: CommandDescriptor<{ frameId: string }>;
  private capturedFrame?: import('../types').FrameInstance;

  constructor(public readonly frameId: string) {
    this.descriptor = {
      id: generateStableId('cmd'),
      type: 'frame.delete',
      label: `Delete Frame`,
      timestamp: Date.now(),
      payload: { frameId },
    };
  }

  execute(state: DomainGraphState): DomainGraphState {
    this.capturedFrame = state.canonical.frames?.[this.frameId];
    if (!this.capturedFrame) return state;

    const frames = { ...state.canonical.frames };
    delete frames[this.frameId];

    return {
      canonical: {
        ...state.canonical,
        frames,
        modifiedAt: Date.now(),
      },
      indexes: state.indexes,
    };
  }

  undo(state: DomainGraphState): DomainGraphState {
    if (!this.capturedFrame) return state;
    const frames = {
      ...(state.canonical.frames || {}),
      [this.capturedFrame.id]: this.capturedFrame,
    };
    return {
      canonical: {
        ...state.canonical,
        frames,
        modifiedAt: Date.now(),
      },
      indexes: state.indexes,
    };
  }
}
